import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePlan from '../hooks/usePlan';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LogService, { LOG_ACTIONS } from '../services/LogService';

// ============================================================
// PLANOS — Mesmo layout e conteúdo do Checkout.js
// Diferença: mostra "Plano atual" se usuário já tem plano
// e redireciona pro Checkout pra pagamento
// ============================================================

const PLANOS = [
  {
    id: 'avulso_cub',
    planoId: 'gratis', // para comparar com plano atual
    nome: 'Orçamento Único',
    preco: 'R$ 49',
    precoCentavos: ',90',
    periodo: 'por orçamento',
    descricao: 'Acesso completo a 1 orçamento único com CUB e Quantitativos de Materiais.',
    features: [
      { text: 'Leitura de planta com IA', included: true },
      { text: 'Orçamento CUB', included: true },
      { text: 'Detalhamento do orçamento por etapas', included: true },
      { text: 'Quantitativo de materiais', included: true },
      { text: 'Exportação para PDF e Excel', included: true },
      { text: 'Personalização de Relatórios', included: false },
      { text: 'Histórico de orçamentos', included: false },
      { text: 'Acesso à Comunidade QCO', included: false },
      { text: 'Perfil Profissional QCO', included: false },
      { text: 'Orçamento SINAPI', included: false },
      { text: 'Curva ABC', included: false },
      { text: 'Curva S', included: false },
      { text: 'Cronograma', included: false },
    ],
    destaque: false,
    btnText: 'Comprar',
  },
  {
    id: 'basico_mensal',
    planoId: 'basico',
    nome: 'Plano Básico',
    preco: 'R$ 69',
    precoCentavos: ',90',
    periodo: '/mês',
    descricao: 'Acesso completo a 5 orçamentos por mês com CUB e Quantitativos de Materiais.',
    subtexto: 'Orçamento extra por R$12,90 cada.',
    features: [
      { text: 'Leitura de planta com IA', included: true },
      { text: 'Orçamento CUB', included: true },
      { text: 'Detalhamento do orçamento por etapas', included: true },
      { text: 'Quantitativo de materiais', included: true },
      { text: 'Exportação para PDF e Excel', included: true },
      { text: 'Personalização de Relatórios', included: true },
      { text: 'Histórico de orçamentos', included: true },
      { text: 'Acesso à Comunidade QCO', included: true, badge: 'em breve' },
      { text: 'Perfil Profissional QCO', included: false, badge: 'em breve' },
      { text: 'Templates de orçamentos QCO', included: false, badge: 'em breve' },
      { text: 'Orçamento SINAPI', included: false, badge: 'em breve' },
      { text: 'Curva ABC', included: false, badge: 'em breve' },
      { text: 'Curva S', included: false, badge: 'em breve' },
      { text: 'Cronograma', included: false, badge: 'em breve' },
    ],
    destaque: false,
    btnText: 'Assinar Básico',
  },
  {
    id: 'profissional_mensal',
    planoId: 'pro',
    nome: 'Plano Profissional',
    preco: 'R$ 119',
    precoCentavos: ',90',
    periodo: '/mês',
    descricao: 'Orçamentos ilimitados com CUB, SINAPI e Quantitativo de Materiais.',
    features: [
      { text: 'Leitura de planta com IA', included: true },
      { text: 'Orçamento CUB', included: true },
      { text: 'Detalhamento do orçamento por etapas', included: true },
      { text: 'Quantitativo de materiais', included: true },
      { text: 'Exportação para PDF e Excel', included: true },
      { text: 'Personalização de Relatórios', included: true },
      { text: 'Histórico de orçamentos', included: true },
      { text: 'Acesso à Comunidade QCO', included: true, badge: 'em breve' },
      { text: 'Perfil Profissional QCO', included: true, badge: 'em breve' },
      { text: 'Templates de orçamentos QCO', included: true, badge: 'em breve' },
      { text: 'Orçamento SINAPI', included: true, badge: 'em breve' },
      { text: 'Curva ABC', included: true, badge: 'em breve' },
      { text: 'Curva S', included: true, badge: 'em breve' },
      { text: 'Cronograma', included: true, badge: 'em breve' },
    ],
    destaque: true,
    btnText: 'Assinar Profissional',
  },
];

function Plans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan: currentPlan } = usePlan();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
    LogService.log(LOG_ACTIONS.VIEW_PLANS);
  }, []);

  const handleSelect = (plano) => {
    LogService.log(LOG_ACTIONS.PAYMENT_ATTEMPT, {
      status: 'info',
      details: { plan: plano.id, planName: plano.nome, price: `${plano.preco}${plano.precoCentavos}` },
    });
    navigate(`/checkout/${plano.id}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-muted/30">
      <Header />
      <main className={`flex-1 px-4 pt-32 pb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="max-w-5xl mx-auto">
          {/* Título */}
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-2">
              Escolha o Plano Ideal
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {user
                ? 'Selecione o plano ideal para seus projetos.'
                : 'Comece grátis, veja o preview do resultado e pague apenas quando quiser o acesso completo.'}
            </p>
          </div>

          {/* Cards de planos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {PLANOS.map((plano) => {
              const isCurrent = plano.planoId === currentPlan?.id;
              return (
                <div
                  key={plano.id}
                  className={`relative bg-card rounded-2xl shadow-card border-2 ${
                    plano.destaque ? 'border-primary' : 'border-border'
                  } p-6 flex flex-col transition-all hover:shadow-lg`}
                >
                  {plano.destaque && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                      Mais popular
                    </div>
                  )}

                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-foreground">{plano.nome}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{plano.descricao}</p>
                  </div>

                  <div className="mb-2">
                    <span className="text-3xl font-display font-bold text-foreground">{plano.preco}</span>
                    <span className="text-lg font-bold text-foreground">{plano.precoCentavos}</span>
                    <span className="text-sm text-muted-foreground ml-1">{plano.periodo}</span>
                  </div>

                  {plano.subtexto && (
                    <p className="text-xs text-muted-foreground mb-4">{plano.subtexto}</p>
                  )}
                  {!plano.subtexto && <div className="mb-4" />}

                  <ul className="space-y-2 mb-6 flex-1">
                    {plano.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2 text-sm ${f.included ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {f.included ? (
                          <svg className="w-4 h-4 text-green-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-red-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        <span>
                          {f.text}
                          {f.badge && (
                            <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                              {f.badge}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <div className="w-full py-3 rounded-xl font-bold text-sm text-center border-2 border-green-500 text-green-600 bg-green-50">
                      Plano atual
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSelect(plano)}
                      className={`w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer ${
                        plano.destaque
                          ? 'bg-primary text-white hover:bg-primary/90 shadow-md'
                          : 'bg-muted text-foreground hover:bg-primary/10 hover:text-primary border border-border'
                      }`}
                    >
                      {plano.btnText}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Garantias */}
          <div className="bg-card rounded-2xl shadow-card p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h4 className="text-sm font-bold text-foreground mb-1">Pagamento seguro</h4>
                <p className="text-xs text-muted-foreground">Via Mercado Pago — PIX, cartão ou boleto</p>
              </div>
              <div>
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h4 className="text-sm font-bold text-foreground mb-1">Cancelamento fácil</h4>
                <p className="text-xs text-muted-foreground">Cancele a qualquer momento, sem multa</p>
              </div>
              <div>
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="text-sm font-bold text-foreground mb-1">Acesso imediato</h4>
                <p className="text-xs text-muted-foreground">Plano ativado automaticamente após pagamento</p>
              </div>
            </div>
          </div>

          {/* Teste grátis */}
          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground">
              Ainda não tem certeza?{' '}
              <button onClick={() => navigate('/orcamento/novo?tipo=cub')} className="text-primary font-semibold hover:underline">
                Teste grátis
              </button>
              {' '}— faça upload da planta e veja um preview do resultado antes de pagar.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Plans;
